<script setup lang="ts">
import { computed } from 'vue';
import BaseIcon, { type IconName } from './BaseIcon.vue';

const props = withDefaults(defineProps<{
  icon: IconName;
  label: string;
  active?: boolean;
  disabled?: boolean;
}>(), {
  active: false,
  disabled: false
});

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

const buttonClass = computed(() => [
  'toggle-button',
  {
    'toggle-button--active': props.active,
    'toggle-button--disabled': props.disabled
  }
]);

function handleClick(event: MouseEvent) {
  if (!props.disabled) {
    emit('click', event);
  }
}
</script>

<template>
  <button
    :class="buttonClass"
    :disabled="disabled"
    @click="handleClick"
  >
    <BaseIcon
      :name="icon"
      :size="32"
      class="toggle-button__icon"
      color="currentColor"
    />
    <span class="toggle-button__label">
      {{ label }}
    </span>
  </button>
</template>

<style scoped>
.toggle-button {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.2s, opacity 0.2s;
  min-width: 90px;

  /* Default inactive state */
  color: rgba(255, 255, 255, 0.7);
}

.toggle-button__icon {
  flex-shrink: 0;
}

.toggle-button__label {
  font-size: var(--font-size-xs);
  font-weight: 400;
  text-align: center;
  line-height: 1.2;
}

.toggle-button--active {
  color: var(--color-text-light);
}

.toggle-button:hover:not(.toggle-button--disabled) {
  color: var(--color-text-light);
}

.toggle-button--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.toggle-button--active::after {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 2px;
  background-color: var(--color-text-light);
}
</style>